import React from 'react';
import classNames from 'classnames';
import { Heading, NamedLink, PropertyGroup } from '../../components';

import css from './ProfilePage.module.css';

const SectionMultiEnumMaybe = props => {
  const {
    heading,
    options,
    selectedOptions,
    showUnselectedOptions = true,
    displayChips = false,
    chipLinkSearchKey,
    onChipClick,
    selectedChipKey,
  } = props;
  const hasContent = showUnselectedOptions || selectedOptions?.length > 0;
  if (!heading || !options || !hasContent) {
    return null;
  }

  // Matterax: chip-style render (opt-in via showConfig.displayChips on the
  // user-field config). Used by the brand-partner `brands` field — a
  // curated list of supplier brands a single account is authorised to
  // publish under (e.g. Stone Empire's account also carries Janesis,
  // Agglotech, Caesarstone, Neolith, Okite). Chips read as badges /
  // brand attribution rather than the amenities-style checkbox list
  // PropertyGroup produces. Falls back to PropertyGroup when displayChips
  // is false so existing multi-enum user fields keep their look.
  //
  // Three render modes for chips, in priority order:
  //   1. onChipClick prop set → render <button>; click toggles selection
  //      via the host-page callback. The host (ProfilePage MainContent)
  //      owns the `selectedChipKey` state and uses it to filter the
  //      listings shown on the same page — no redirect.
  //   2. chipLinkSearchKey set → render <NamedLink> to SearchPage with
  //      ?{chipLinkSearchKey}={option.key}. For brands that becomes
  //      /s?pub_brand_slug=janesis. Kept as the legacy redirect path
  //      and as a fallback when no host-side click handler exists.
  //   3. Neither → static <li>, no interaction.
  if (displayChips) {
    const selectedSet = new Set(selectedOptions || []);
    const items = options.filter(o => selectedSet.has(o.key));
    if (items.length === 0) {
      return null;
    }
    return (
      <div className={css.sectionMultiEnum}>
        <Heading as="h2" rootClassName={css.sectionHeading}>
          {heading}
        </Heading>
        <ul className={css.chips}>
          {items.map(item => {
            if (onChipClick) {
              const isActive = selectedChipKey === item.key;
              const isOtherActive = selectedChipKey && !isActive;
              return (
                <li key={item.key} className={css.chipItem}>
                  <button
                    type="button"
                    className={classNames(css.chipButton, {
                      [css.chipButtonActive]: isActive,
                      [css.chipButtonDimmed]: isOtherActive,
                    })}
                    aria-pressed={isActive}
                    onClick={() => onChipClick(item.key)}
                  >
                    {item.label}
                  </button>
                </li>
              );
            }
            if (chipLinkSearchKey) {
              return (
                <li key={item.key} className={css.chipItem}>
                  <NamedLink
                    className={css.chipLink}
                    name="SearchPage"
                    to={{ search: `?${chipLinkSearchKey}=${encodeURIComponent(item.key)}` }}
                  >
                    {item.label}
                  </NamedLink>
                </li>
              );
            }
            return (
              <li key={item.key} className={css.chip}>
                {item.label}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className={css.sectionMultiEnum}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        {heading}
      </Heading>
      <PropertyGroup
        id="ListingPage.amenities"
        options={options}
        selectedOptions={selectedOptions}
        twoColumns={options.length > 5}
        showUnselectedOptions={showUnselectedOptions}
      />
    </div>
  );
};

export default SectionMultiEnumMaybe;
